import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { createEnumParam, useQueryParam, withDefault } from 'use-query-params';

import { OfflineTimeInsight } from 'apis/merchant-gateway/insights';
import { useThemeMode } from 'common/themeMode';
import { CustomLegends, Legend, LegendChartType } from 'modules/experience-core';

import OfflineTimePerDay from './OfflineTimePerDay';
import { OfflineTimePerHour } from './OfflineTimePerHour';
import OfflineTimeSummary from './OfflineTimeSummary';
import { AlivenessMetricCategory, OfflineInterval, OfflineMinutesPerHour } from './types';
import { InsightEntity, InsightResponseWithPreviousInterval } from '../../../types';
import ErrorView from '../../ErrorView';
import LoadingView from '../../LoadingView';

type OfflineTimeProps = {
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
  insightEntityType: InsightEntity['type'];
};

const OfflineTimeContainer = styled.div`
  position: relative;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;

  min-height: 373px;

  display: flex;
  flex-direction: column;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const OfflineTime = ({ offlineTimeInsight, insightEntityType }: OfflineTimeProps) => {
  const { themeMode } = useThemeMode();
  const { formatMessage } = useIntl();

  const [offlineInterval, setOfflineInterval] = useQueryParam(
    'offlineInterval',
    withDefault(createEnumParam(Object.values(OfflineInterval)), OfflineInterval.DAY, true),
    {
      removeDefaultsFromUrl: true,
    },
  );

  const offlineTimePerDayLegends: Record<AlivenessMetricCategory, Legend> = {
    [AlivenessMetricCategory.AVAILABILITY]: {
      value: formatMessage({ id: 'insights.offline-time.availability' }),
      color: themeMode === 'light' ? colors.pepper8 : colors.salt16,
      type: LegendChartType.BAR,
    },
    [AlivenessMetricCategory.NON_ALIVE]: {
      value: formatMessage({ id: 'insights.offline-time.offline-due-to-issue' }),
      color: colors.orange48,
      type: LegendChartType.BAR,
    },
    [AlivenessMetricCategory.OFFLINE_MANUALLY]: {
      value: formatMessage({ id: 'insights.offline-time.manually-put-into-offline' }),
      color: colors.orange80,
      type: LegendChartType.BAR,
    },
  };

  const OfflineMinutesPerHourLegends: Record<OfflineMinutesPerHour, Legend> = {
    [OfflineMinutesPerHour.OFFLINE_DUE_TO_ISSUE]: {
      value: formatMessage({ id: 'insights.offline-time.offline-due-to-issue' }),
      color: colors.orange100,
      type: LegendChartType.BAR,
    },
    [OfflineMinutesPerHour.MANUALLY_PUT_OFFLINE]: {
      value: formatMessage({ id: 'insights.offline-time.manually-put-into-offline' }),
      color: colors.banana100,
      type: LegendChartType.BAR,
    },
  };

  const isBrandOrEnterpriseEntity = ['enterprise', 'brand'].includes(insightEntityType);

  return (
    <OfflineTimeContainer>
      {offlineTimeInsight.isFetching ? (
        <LoadingView />
      ) : offlineTimeInsight.isError ? (
        <ErrorView onRetry={offlineTimeInsight.refetch} />
      ) : (
        <>
          <OfflineTimeSummary offlineTimeInsight={offlineTimeInsight}>
            {isBrandOrEnterpriseEntity ? (
              <CustomLegends legends={Object.values(OfflineMinutesPerHourLegends)} />
            ) : (
              <>
                {offlineInterval === OfflineInterval.DAY && (
                  <CustomLegends legends={Object.values(offlineTimePerDayLegends)} />
                )}
                {offlineInterval === OfflineInterval.HOUR && (
                  <CustomLegends legends={Object.values(OfflineMinutesPerHourLegends)} />
                )}
              </>
            )}
          </OfflineTimeSummary>
          <Divider />
          {isBrandOrEnterpriseEntity ? (
            <OfflineTimePerDay
              insightEntityType={insightEntityType}
              offlineTimeInsight={offlineTimeInsight}
              offlineInterval={offlineInterval}
              setOfflineInterval={setOfflineInterval}
              legends={offlineTimePerDayLegends}
            />
          ) : (
            <>
              {offlineInterval === OfflineInterval.DAY && (
                <OfflineTimePerDay
                  insightEntityType={insightEntityType}
                  offlineTimeInsight={offlineTimeInsight}
                  offlineInterval={offlineInterval}
                  setOfflineInterval={setOfflineInterval}
                  legends={offlineTimePerDayLegends}
                />
              )}
              {offlineInterval === OfflineInterval.HOUR && (
                <OfflineTimePerHour
                  insightEntityType={insightEntityType}
                  offlineTimeInsight={offlineTimeInsight}
                  offlineInterval={offlineInterval}
                  setOfflineInterval={setOfflineInterval}
                  legends={OfflineMinutesPerHourLegends}
                />
              )}
            </>
          )}
        </>
      )}
    </OfflineTimeContainer>
  );
};

export default OfflineTime;
